// Query keys + fetchers for the GREEN conversion pools (SM-468).
//
// Byte-for-byte the same endpoints web-backoffice uses (SM-467), on
// purpose: the phase's first compatibility invariant is that Backoffice
// and MM Web show IDENTICAL pool balances and movement results for the
// same pool record. Two consoles reading one authoritative contract is
// how that stops being a promise and starts being structural — there is
// no MM-specific pool read to drift from.
//
// MM Web reaches `/admin/*` through the same api-gateway and the same
// admin session as Backoffice, so the permission gate (backoffice-service
// AND ledger) applies identically here.

import { api, qs } from "./api.ts";
import type { PageInfo } from "./types.ts";
import type {
  ConversionPoolStatus,
  ConversionPoolStatusResponse,
  ConversionReconResponse,
  ConversionReconRouteTotals,
  ConversionRouteConfig,
  ConversionRouteConfigListResponse,
  FundPoolInput,
  PoolMovementItem,
  RebalancePoolInput,
} from "./currency-types.ts";

export const currencyKeys = {
  all: ["currency"] as const,
  routes: () => [...currencyKeys.all, "conversion-routes"] as const,
  pools: () => [...currencyKeys.all, "conversion-pools"] as const,
  poolMovements: (currency: string, offset: number, limit: number) =>
    [...currencyKeys.all, "pool-movements", currency, offset, limit] as const,
  recon: () => [...currencyKeys.all, "conversion-recon"] as const,
};

/** Invalidation prefixes for the pool families (SM-563). */
export const currencyKeyPrefixes = {
  pools: [...currencyKeys.all, "conversion-pools"] as const,
  routes: [...currencyKeys.all, "conversion-routes"] as const,
  poolMovements: [...currencyKeys.all, "pool-movements"] as const,
  recon: [...currencyKeys.all, "conversion-recon"] as const,
};

export const fetchConversionRoutes = () =>
  api<ConversionRouteConfigListResponse>("/admin/conversions/routes").then<ConversionRouteConfig[]>(
    (r) => r.routes ?? [],
  );

export const fetchConversionPools = () =>
  api<ConversionPoolStatusResponse>("/admin/conversions/pools").then<ConversionPoolStatus[]>(
    (r) => r.pools ?? [],
  );

export const fetchConversionRecon = () =>
  api<ConversionReconResponse>("/admin/conversions/recon").then<ConversionReconRouteTotals[]>(
    (r) => r.routes ?? [],
  );

export const fetchPoolMovements = (
  currency: string,
  opts: { limit: number; offset: number },
) =>
  api<{ movements: PoolMovementItem[]; page?: PageInfo }>(
    `/admin/conversions/pools/${encodeURIComponent(currency)}/movements${qs({
      limit: opts.limit,
      offset: opts.offset,
    })}`,
  ).then<PoolMovementItem[]>((r) => r.movements ?? []);

/**
 * Fund / rebalance carry the operator's idempotency reference in the
 * BODY (`operation_id`), which is what ledger's duplicate guard keys on.
 * The caller mints it once per confirm — not per attempt — so a retry
 * after a lost response resolves the original movement instead of making
 * a second one.
 */
export const fundConversionPool = (currency: string, input: FundPoolInput) =>
  api<PoolMovementItem>(`/admin/conversions/pools/${encodeURIComponent(currency)}/fund`, {
    method: "POST",
    body: input,
  });

export const rebalanceConversionPool = (currency: string, input: RebalancePoolInput) =>
  api<PoolMovementItem>(`/admin/conversions/pools/${encodeURIComponent(currency)}/rebalance`, {
    method: "POST",
    body: input,
  });

// Route configuration — enable/disable, fee, limits, rate versions —
// is deliberately NOT here. MM Web monitors and rebalances liquidity;
// changing what a user converts at is a policy action and stays a
// Backoffice-only mutation (phase decision log #7). Adding it here
// would give two consoles the same policy lever with no shared
// confirmation between them.
